import React from 'react';
import { BaseHero, Text } from '@blueprint/ui';
import ErrorImage from './ErrorImage';
import { logError } from 'shared/logger';

type Props = {
    children: React.ReactNode;
    renderOnError?: React.ReactNode | null;
};

type State = {
    hasError: boolean;
};

class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        logError(error, errorInfo);
    }

    render() {
        const hasDefinedOrNullRenderOnError =
            this.props.renderOnError || this.props.renderOnError === null;

        if (this.state.hasError) {
            return hasDefinedOrNullRenderOnError ? (
                this.props.renderOnError
            ) : (
                <BaseHero
                    title="Something went wrong"
                    image={ErrorImage}
                    content={
                        <Text>
                            Please refresh your page. If the issue continues,
                            please try again later.
                        </Text>
                    }
                />
            );
        }
        return this.props.children;
    }
}
export default ErrorBoundary;

export function withErrorBoundary<P>(
    WrappedComponent: React.ComponentType<P>,
    renderOnError: React.ReactNode
): React.ComponentType<P> {
    return (props: P) => (
        <ErrorBoundary renderOnError={renderOnError}>
            <WrappedComponent {...props} />
        </ErrorBoundary>
    );
}
